import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  FolderTree,
  Pencil,
  Plus,
  Search,
  Sparkles,
  UserRound,
  X,
} from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  PageHeader,
  TableSkeleton,
} from "../components/ui";
import { formatDate, humanizeEnum } from "../lib/format";
import { ownerFirst } from "../lib/orgChart";
import { isAccountAdmin } from "../lib/people";
import {
  PEOPLE_METRIC_BLURB,
  PEOPLE_METRIC_LABEL,
  isPeopleMetric,
  metricInputsFrom,
  peopleMetricIds,
  type PeopleMetric,
} from "../lib/peopleMetrics";
import type { EmployeeFull } from "../api/types";

const STATUSES = ["", "active", "notice_period", "exited", "on_leave"];
const STATUS_LABEL: Record<string, string> = {
  active: "Active",
  notice_period: "Notice period",
  exited: "Exited",
  on_leave: "On leave",
};

const LOGIN_META: Record<
  string,
  { label: string; tone: "slate" | "emerald" | "amber" | "rose" }
> = {
  none: { label: "No login", tone: "slate" },
  pending: { label: "Invited", tone: "amber" },
  accepted: { label: "Active", tone: "emerald" },
  expired: { label: "Expired", tone: "rose" },
  revoked: { label: "Revoked", tone: "rose" },
  failed: { label: "Failed", tone: "rose" },
};

/**
 * The employment register.
 *
 * Not the directory: that is who can be picked in a dropdown. This is the
 * record an admin maintains -- department, PF/ESI, contact details -- with pay
 * deliberately absent (salary lives behind manager-only screens).
 *
 * It also backs the People dashboard's metric cards: `?metric=` filters the
 * same rows through `lib/peopleMetrics`, so the count on a card and the list it
 * opens are one computation. The filter is shown and can be cleared back to the
 * full register.
 */
export function EmployeesPage() {
  const { user } = useAuth();
  const canManage = isAccountAdmin(user?.role);
  const timezone = user?.account?.timezone;
  const queryClient = useQueryClient();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [bulkResult, setBulkResult] = useState<any | null>(null);
  const [inviteActionMessage, setInviteActionMessage] = useState<string | null>(null);

  const [params, setParams] = useSearchParams();
  const search = params.get("search") ?? "";
  const status = params.get("status") ?? "";
  const cursor = params.get("cursor") ?? "";
  const metricParam = params.get("metric");
  const metric: PeopleMetric | null = isPeopleMetric(metricParam) ? metricParam : null;
  const date = params.get("date") || localDate(timezone);

  const [draft, setDraft] = useState(search);
  const debounceTimer = useRef<number | undefined>(undefined);
  useEffect(() => setDraft(search), [search]);
  useEffect(() => () => window.clearTimeout(debounceTimer.current), []);

  function update(next: Record<string, string>) {
    setParams(
      (prev) => {
        const merged = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(next)) {
          if (value) merged.set(key, value);
          else merged.delete(key);
        }
        return merged;
      },
      { replace: true },
    );
  }

  // The everyday register: server-paginated.
  const listQuery = useQuery({
    queryKey: ["employees", search, status, cursor],
    queryFn: ({ signal }) =>
      api.employeeList(
        {
          search: search || undefined,
          status: status || undefined,
          cursor: cursor || undefined,
          page_size: 25,
          ordering: "last_name",
        },
        signal,
      ),
    placeholderData: (prev) => prev,
    enabled: !metric,
  });

  // The metric view: one wide read of each source, filtered in the client so a
  // derived list ("not checked in") is expressible and matches the card.
  const metricEmployees = useQuery({
    queryKey: ["employees", "metric"],
    queryFn: ({ signal }) => api.employeeList({ page_size: 200, ordering: "last_name" }, signal),
    enabled: Boolean(metric),
  });
  const metricAttendance = useQuery({
    queryKey: ["employees", "metric", "attendance", date],
    queryFn: ({ signal }) => api.attendancePage({ date, page_size: 200 }, signal),
    enabled: Boolean(metric),
  });
  const metricLeave = useQuery({
    queryKey: ["employees", "metric", "leave"],
    queryFn: ({ signal }) => api.leaveRequests({ status: "approved", page_size: 200 }, signal),
    enabled: Boolean(metric),
  });
  const metricShifts = useQuery({
    queryKey: ["employees", "metric", "shifts", date],
    queryFn: ({ signal }) => api.shiftsPage({ from: date, to: date, page_size: 200 }, signal),
    enabled: Boolean(metric),
  });

  const metricIds = useMemo(() => {
    if (!metric) return null;
    return peopleMetricIds(
      metric,
      metricInputsFrom({
        employees: metricEmployees.data?.results ?? [],
        attendance: metricAttendance.data?.results ?? [],
        leave: metricLeave.data?.results ?? [],
        shifts: metricShifts.data?.results ?? [],
      }),
      date,
    );
  }, [metric, date, metricEmployees.data, metricAttendance.data, metricLeave.data, metricShifts.data]);

  const baseEmployees: EmployeeFull[] = metric
    ? (metricEmployees.data?.results ?? [])
    : (listQuery.data?.results ?? []);
  // The owner leads the register, matching the Organization chart. Search,
  // status filters, pagination and profile links are untouched: only the order
  // of the rows already on screen changes.
  const employees = ownerFirst(
    metric && metricIds
      ? baseEmployees.filter((employee) => metricIds.has(employee.id))
      : baseEmployees,
  );
  const nextCursor = cursorFrom(listQuery.data?.next);

  const isPending = metric
    ? metricEmployees.isPending || metricAttendance.isPending || metricLeave.isPending || metricShifts.isPending
    : listQuery.isPending;
  const queryError = metric
    ? metricEmployees.error || metricAttendance.error || metricLeave.error || metricShifts.error
    : listQuery.error;

  const deactivate = useMutation({
    mutationFn: (id: string) => api.deactivateEmployee(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["employees"] }),
  });

  const reactivate = useMutation({
    mutationFn: (id: string) => api.reactivateEmployee(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["employees"] }),
  });

  const generateInvites = useMutation({
    mutationFn: () => api.generateEmployeeInvites(),
    onSuccess: (data) => {
      setBulkResult(data);
      void queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });

  const resendInvite = useMutation({
    mutationFn: (id: string) => api.resendEmployeeInvite(id),
    onSuccess: () => {
      setInviteActionMessage("Invitation link updated successfully.");
      setTimeout(() => setInviteActionMessage(null), 3000);
      void queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });

  const revokeInvite = useMutation({
    mutationFn: (id: string) => api.revokeEmployeeInvite(id),
    onSuccess: () => {
      setInviteActionMessage("Invitation link revoked.");
      setTimeout(() => setInviteActionMessage(null), 3000);
      void queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });

  return (
    <>
      <PageHeader
        title="Employees"
        subtitle={
          isPending
            ? metric
              ? "Filtering…"
              : "Employment records, separate from who can log in."
            : metric
              ? `${employees.length} shown · ${PEOPLE_METRIC_LABEL[metric]}`
              : `${employees.length} shown${listQuery.data?.next ? ", more available" : ""}`
        }
        actions={
          canManage ? (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                disabled={generateInvites.isPending}
                onClick={() => generateInvites.mutate()}
                className="inline-flex items-center gap-1.5"
              >
                <Sparkles size={15} /> {generateInvites.isPending ? "Generating…" : "Generate invites"}
              </Button>
              <Link
                to="/people/organization"
                className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
              >
                <FolderTree size={15} /> Organization
              </Link>
              <Link
                to="/people/employees/new"
                className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
              >
                <Plus size={16} /> Add employee
              </Link>
            </div>
          ) : null
        }
      />

      {inviteActionMessage ? (
        <div className="mb-4 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {inviteActionMessage}
        </div>
      ) : null}

      {bulkResult ? (
        <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Generated {bulkResult.generated_count} Invitation Link{bulkResult.generated_count === 1 ? "" : "s"}
              </h3>
              <p className="text-xs text-slate-500">
                Share these individual links with employees to allow them to create their passwords.
              </p>
            </div>
            <button
              onClick={() => setBulkResult(null)}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
          <div className="mt-3 max-h-60 divide-y divide-slate-100 overflow-y-auto">
            {bulkResult.invitations.map((inv: any) => (
              <div key={inv.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-xs">
                <div>
                  <span className="font-medium text-slate-900">{inv.display_name}</span>{" "}
                  <span className="text-slate-500">({inv.email})</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={inv.invitation_url}
                    className="w-64 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700 select-all"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(inv.invitation_url);
                      setCopiedId(inv.id);
                      setTimeout(() => setCopiedId(null), 2000);
                    }}
                  >
                    {copiedId === inv.id ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                    {copiedId === inv.id ? "Copied" : "Copy"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {metric ? (
        <div
          role="status"
          className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-xs"
        >
          <span className="text-sm text-slate-700">
            Showing <span className="font-medium">{PEOPLE_METRIC_LABEL[metric]}</span> ·{" "}
            <span className="tabular-nums text-slate-500">{formatDate(date, timezone)}</span>
          </span>
          <span className="hidden text-xs text-slate-500 sm:inline">{PEOPLE_METRIC_BLURB[metric]}</span>
          <Link
            to="/people/employees"
            className="ml-auto inline-flex items-center gap-1 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
          >
            Clear filter
          </Link>
        </div>
      ) : (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative min-w-56 flex-1">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="search"
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                window.clearTimeout(debounceTimer.current);
                debounceTimer.current = window.setTimeout(() => {
                  update({ search: event.target.value, cursor: "" });
                }, 250);
              }}
              placeholder="Search by name, code or role"
              aria-label="Search employees"
              className="w-full rounded-md border-0 bg-white py-2 pl-9 pr-3 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {STATUSES.map((value) => (
              <button
                key={value || "all"}
                onClick={() => update({ status: value, cursor: "" })}
                className={
                  status === value
                    ? "rounded-full bg-slate-900 px-3 py-1.5 text-xs font-medium text-white"
                    : "rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
                }
              >
                {value ? STATUS_LABEL[value] : "All"}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-xs">
        {isPending ? (
          <TableSkeleton rows={8} />
        ) : queryError ? (
          <ErrorState error={queryError} />
        ) : employees.length === 0 ? (
          <EmptyState
            icon={<UserRound size={28} />}
            title={
              metric
                ? `No one matches “${PEOPLE_METRIC_LABEL[metric]}”`
                : search || status
                  ? "Nothing matches that"
                  : "No employees yet"
            }
            body={
              metric
                ? `Nobody in this scope is ${PEOPLE_METRIC_LABEL[metric].toLowerCase()} on ${formatDate(date, timezone)}.`
                : search || status
                  ? "Try a different search, or clear the filters."
                  : "Add the people who work here; their attendance and pay hang off these records."
            }
            action={
              metric ? (
                <Link
                  to="/people/employees"
                  className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
                >
                  Clear filter
                </Link>
              ) : undefined
            }
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="w-12 px-4 py-2.5" />
                <th className="px-4 py-2.5">Employee</th>
                <th className="hidden px-4 py-2.5 lg:table-cell">ID</th>
                <th className="hidden px-4 py-2.5 md:table-cell">Designation</th>
                <th className="hidden px-4 py-2.5 xl:table-cell">Department</th>
                <th className="hidden px-4 py-2.5 xl:table-cell">Branch</th>
                <th className="hidden px-4 py-2.5 lg:table-cell">Type</th>
                <th className="hidden px-4 py-2.5 xl:table-cell">Manager</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Login</th>
                {canManage ? <th className="px-4 py-2.5 text-right" /> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {employees.map((employee) => (
                <tr key={employee.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link to={`/people/employees/${employee.id}`} aria-label={`Open ${employee.display_name}`}>
                      <Avatar name={employee.display_name} src={employee.photo} size={40} />
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/people/employees/${employee.id}`} className="block max-w-xs">
                      <span className="block truncate text-sm font-medium text-slate-900">
                        {employee.display_name}
                      </span>
                      <span className="block truncate text-xs text-slate-500">
                        {employee.email || employee.designation || "No contact details"}
                      </span>
                    </Link>
                  </td>
                  <td className="hidden px-4 py-3 text-sm tabular-nums text-slate-600 lg:table-cell">
                    {employee.employee_code}
                  </td>
                  <td className="hidden px-4 py-3 text-sm text-slate-600 md:table-cell">
                    {employee.designation || "Not set"}
                  </td>
                  <td className="hidden px-4 py-3 text-sm text-slate-600 xl:table-cell">
                    {employee.department_name || "No department"}
                  </td>
                  <td className="hidden px-4 py-3 text-sm text-slate-600 xl:table-cell">
                    {employee.branch_name || "No branch"}
                  </td>
                  <td className="hidden px-4 py-3 text-sm text-slate-600 lg:table-cell">
                    {employee.employment_type ? humanizeEnum(employee.employment_type) : "Not set"}
                  </td>
                  <td className="hidden px-4 py-3 text-sm text-slate-600 xl:table-cell">
                    {employee.reporting_to_name || "No manager"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={employee.status === "active" ? "emerald" : "slate"}>
                      {STATUS_LABEL[employee.status ?? ""] ?? employee.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Badge tone={LOGIN_META[employee.invite_status]?.tone ?? "slate"}>
                        {LOGIN_META[employee.invite_status]?.label ?? (employee.has_login ? "Active" : "No login")}
                      </Badge>
                      {employee.invitation_url ? (
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(employee.invitation_url!);
                            setCopiedId(employee.id);
                            setTimeout(() => setCopiedId(null), 2000);
                          }}
                          title="Copy invitation link"
                          className="inline-flex size-6 items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                        >
                          {copiedId === employee.id ? (
                            <Check size={12} className="text-emerald-600" />
                          ) : (
                            <Copy size={12} />
                          )}
                        </button>
                      ) : null}
                    </div>
                  </td>
                  {canManage ? (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {employee.invitation_url ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Revoke invitation link"
                            disabled={revokeInvite.isPending}
                            onClick={() => revokeInvite.mutate(employee.id)}
                            className="text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                          >
                            Revoke
                          </Button>
                        ) : employee.invite_status === "revoked" || employee.invite_status === "expired" ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Resend invitation link"
                            disabled={resendInvite.isPending}
                            onClick={() => resendInvite.mutate(employee.id)}
                            className="text-xs"
                          >
                            Resend
                          </Button>
                        ) : !employee.has_login || employee.invite_status === "none" ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Generate invitation link"
                            disabled={resendInvite.isPending}
                            onClick={() => resendInvite.mutate(employee.id)}
                            className="text-xs"
                          >
                            Invite
                          </Button>
                        ) : null}
                        {employee.status === "exited" ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={reactivate.isPending}
                            onClick={() => reactivate.mutate(employee.id)}
                            className="text-xs"
                          >
                            Reactivate
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={deactivate.isPending}
                            onClick={() => deactivate.mutate(employee.id)}
                            className="text-xs"
                          >
                            Deactivate
                          </Button>
                        )}
                        <Link
                          to={`/people/employees/${employee.id}/edit`}
                          aria-label={`Edit ${employee.display_name}`}
                          title="Edit employee"
                          className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
                        >
                          <Pencil size={15} aria-hidden="true" />
                        </Link>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!metric ? (
        <nav className="mt-4 flex items-center justify-between">
          <Button onClick={() => update({ cursor: "" })} disabled={!cursor || listQuery.isFetching}>
            <ChevronLeft size={16} /> First
          </Button>
          <span className="text-xs text-slate-500">
            {listQuery.isFetching ? "Loading…" : cursor ? "Next page" : "First page"}
          </span>
          <Button
            onClick={() => nextCursor && update({ cursor: nextCursor })}
            disabled={!nextCursor || listQuery.isFetching}
          >
            Next <ChevronRight size={16} />
          </Button>
        </nav>
      ) : null}
    </>
  );
}

function cursorFrom(next: string | null | undefined): string | undefined {
  if (!next) return undefined;
  return new URL(next, window.location.origin).searchParams.get("cursor") ?? undefined;
}

/** The account's "today", as YYYY-MM-DD -- not the browser's, not UTC's. */
function localDate(timeZone?: string | null): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timeZone ?? undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function ErrorState({ error }: { error: unknown }) {
  if (!(error instanceof ApiError)) {
    return <EmptyState title="Something went wrong" body="Please try again." />;
  }
  return (
    <EmptyState
      title={error.isPlanLimit ? "Plan limit reached" : "Could not load employees"}
      body={error.message}
    />
  );
}
